import { createSlice } from "@reduxjs/toolkit";

import {
  thinksToDoReducer,
  initialState,
  addJobClosureRejectCommentAction,
  approveOrRejectRegularizeAction,
  getJobClosureListAction,
  getJobClosureRMListAction,
  getJobClosureRatingListAction,
  getRegularizeApprovalPendingListAction,
  getScheduledUpcomingInterviewsList,
  getUnApprovedInvoiceCountListAction,
  getWeeklyApprovedUsersInvoiceEntriesCountAction,
  sendApprovalStatusAction,
  singleGetApprovalListAction,
  singleScheduledUpcomingInterviewsDetailsAction,
  updateRecruitmentTrackerAction,
  submitResignationFeedbackAction,
} from "@/redux/thinksToDo";
import type { ThinksToDoType } from "@/redux/thinksToDo/thinksToDoType";

export const thinksToDoSlice = createSlice({
  name: "thinkToDo",
  initialState,
  reducers: thinksToDoReducer,

  extraReducers: (builder) => {
    builder.addCase(
      getScheduledUpcomingInterviewsList.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getScheduledUpcomingInterviewsList.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          scheduledUpcomingInterviews: result?.result,
          totalCount: result?.total_count,
        };
      }
    );

    builder.addCase(
      getScheduledUpcomingInterviewsList.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      singleScheduledUpcomingInterviewsDetailsAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      singleScheduledUpcomingInterviewsDetailsAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          singleScheduledUpcomingInterviewsDetails: result,
        };
      }
    );

    builder.addCase(
      singleScheduledUpcomingInterviewsDetailsAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      updateRecruitmentTrackerAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        updateFetching: true,
      })
    );

    builder.addCase(
      updateRecruitmentTrackerAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            error: message,
            success,
            statusCode: status_code,
            updateFetching: false,
          };
        }
        return {
          ...state,
          updateFetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      updateRecruitmentTrackerAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      sendApprovalStatusAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        updateFetching: true,
      })
    );

    builder.addCase(
      sendApprovalStatusAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            error: message,
            success,
            statusCode: status_code,
            updateFetching: false,
          };
        }
        return {
          ...state,
          updateFetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      sendApprovalStatusAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        updateFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      singleGetApprovalListAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        updateFetching: true,
      })
    );

    builder.addCase(
      singleGetApprovalListAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, status_code, message, result } = payload;

        if (success === 0) {
          return {
            ...state,
            error: message,
            success,
            statusCode: status_code,
            updateFetching: false,
          };
        }
        return {
          ...state,
          updateFetching: false,
          statusCode: status_code,
          success,
          message,
          singleApprovalData: result,
        };
      }
    );

    builder.addCase(
      singleGetApprovalListAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        updateFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getJobClosureListAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getJobClosureListAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          jobClosureListData: result,
          jobClosureTotalCount: count,
        };
      }
    );

    builder.addCase(
      getJobClosureListAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      addJobClosureRejectCommentAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        updateFetching: true,
      })
    );

    builder.addCase(
      addJobClosureRejectCommentAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            error: message,
            success,
            statusCode: status_code,
            updateFetching: false,
          };
        }
        return {
          ...state,
          updateFetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      addJobClosureRejectCommentAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        updateFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      getJobClosureRMListAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getJobClosureRMListAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          jobClosureListForRM: result,
          totalCountRM: count,
        };
      }
    );

    builder.addCase(
      getJobClosureRMListAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getJobClosureRatingListAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getJobClosureRatingListAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, message, result, status_code, count } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          jobClosureRatingList: result,
        };
      }
    );

    builder.addCase(
      getJobClosureRatingListAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getUnApprovedInvoiceCountListAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getUnApprovedInvoiceCountListAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const {
          success,
          message,
          result,
          status_code,
          count,
          is_accounting_in_charge,
        } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          unApprovedInvoiceCountData: result,
          isAccountingInCharge: is_accounting_in_charge,
        };
      }
    );

    builder.addCase(
      getUnApprovedInvoiceCountListAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getWeeklyApprovedUsersInvoiceEntriesCountAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getWeeklyApprovedUsersInvoiceEntriesCountAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const {
          success,
          message,
          total_count,
          status_code,
          total_target_entries,
        } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          approvedInvoiceTotalCount: total_count ? +total_count : 0,
          usersWeeklyApprovedInvoiceTotalCount: total_target_entries,
        };
      }
    );

    builder.addCase(
      getWeeklyApprovedUsersInvoiceEntriesCountAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      getRegularizeApprovalPendingListAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        fetching: true,
      })
    );

    builder.addCase(
      getRegularizeApprovalPendingListAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, message, result, status_code } = payload;
        if (success === 0) {
          return {
            ...state,
            fetching: false,
            success: 0,
            error: message,
            statusCode: status_code,
          };
        }
        return {
          ...state,
          fetching: false,
          success,
          regularizeApprovalPendingList: result,
        };
      }
    );

    builder.addCase(
      getRegularizeApprovalPendingListAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        fetching: false,
        success: 0,
        error: error.message ?? null,
      })
    );

    builder.addCase(
      approveOrRejectRegularizeAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        updateFetching: true,
      })
    );

    builder.addCase(
      approveOrRejectRegularizeAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            error: message,
            success,
            statusCode: status_code,
            updateFetching: false,
          };
        }
        return {
          ...state,
          updateFetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      approveOrRejectRegularizeAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        updateFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );

    builder.addCase(
      submitResignationFeedbackAction.pending,
      (state: ThinksToDoType) => ({
        ...state,
        updateFetching: true,
      })
    );

    builder.addCase(
      submitResignationFeedbackAction.fulfilled,
      (state: ThinksToDoType, { payload }) => {
        const { success, status_code, message } = payload;

        if (success === 0) {
          return {
            ...state,
            error: message,
            success,
            statusCode: status_code,
            updateFetching: false,
          };
        }
        return {
          ...state,
          updateFetching: false,
          statusCode: status_code,
          success,
          message,
        };
      }
    );

    builder.addCase(
      submitResignationFeedbackAction.rejected,
      (state: ThinksToDoType, { error }) => ({
        ...state,
        updateFetching: false,
        success: 0,
        error: error?.message ?? null,
      })
    );
  },
});

export const {
  resetScheduledUpcomingInterviewsList,
  resetThinksToDoSuccess,
  resetThinksToDoError,
  resetThinksToDoMessage,
  resetThinksToDoStatusCode,
  resetThinksToDoUpdateFetching,
  resetJobClosureListData,
  resetJobClosureTotalCount,
  resetJobClosureListForRM,
  resetTotalCountRM,
  resetJobClosureRatingList,
  resetUnApprovedInvoiceCountData,
  resetUnApprovedInvoiceTotalCount,
  resetUsersWeeklyApprovedInvoiceTotalCount,
  resetRegularizeApprovalPendingList,
} = thinksToDoSlice.actions;
